import { API_DIRECTORY_THEME } from "./constants";

const PLAN_TYPE = {
  ADVANCED: "advanced",
  TRANSACTIONAL: "tiered",
  SUBSCRIPTION: "subscription",
  FREE: "free",
  CUSTOM: "custom",
  BUNDLE_SUBSCRIPTION: "bundle_subscription"
};

const getPlanCardText = apiDirectoryTheme => {
  if (apiDirectoryTheme === API_DIRECTORY_THEME.DEALER_API_DIRECTORY)
    return PLAN_CARD_TEXT_DEALER;
  else if (apiDirectoryTheme === API_DIRECTORY_THEME.ISV_API_DIRECTORY)
    return PLAN_CARD_TEXT_ISV;
};

const PLAN_CARD_TEXT_DEALER = {
  [PLAN_TYPE.ADVANCED]: {
    heading: "Subscription with Overage",
    subtext: ""
  },
  [PLAN_TYPE.TRANSACTIONAL]: {
    heading: "Transactional",
    subtext: "Transparent tiered pricing - Pay only for the value you use"
  },
  [PLAN_TYPE.SUBSCRIPTION]: {
    heading: "Subscription",
    subtext:
      "Monthly plan for unlimited API use per dealership location. Flat rate. "
  },
  [PLAN_TYPE.FREE]: {
    heading: "Free",
    subtext: "Kickstart your journey with a zero-cost free plan"
  },
  [PLAN_TYPE.BUNDLE_SUBSCRIPTION]: {
    heading: "Dealer Plan",
    subtext:
      "Our monthly plan for unlimited API use per dealership location. Flat rate."
  }
};

const PLAN_CARD_TEXT_ISV = {
  [PLAN_TYPE.ADVANCED]: {
    heading: "Subscription with Overage Plans",
    subtext: ""
  },
  [PLAN_TYPE.TRANSACTIONAL]: {
    heading: "Transactional Plan",
    subtext: "Based on monthly call quotas."
  },
  [PLAN_TYPE.SUBSCRIPTION]: {
    heading: "Subscription Plan",
    subtext:
      "Monthly flat rate plan for unlimited use across all dealership locations."
  },
  [PLAN_TYPE.FREE]: {
    heading: "Free Plan",
    subtext: "Unlimited free API calls per month with this plan."
  },
  [PLAN_TYPE.BUNDLE_SUBSCRIPTION]: {
    heading: "",
    subtext: "Our monthly plan for unlimited API use. Flat rate."
  }
};

const PLAN_STATUSES = {
  RETIRED: "retired",
  ACTIVE: "active",
  INACTIVE: "Inactive"
};

const PLAN_VISIBILITY = {
  PUBLIC: "public",
  PRIVATE: "private"
};

const getPlanName = (planType, belongsTo, planName) => {
  if (!planType) {
    return "";
  }

  switch (planType.toLowerCase()) {
    case PLAN_TYPE.SUBSCRIPTION:
      return "Subscription";
    case PLAN_TYPE.TRANSACTIONAL:
      return "Transactional";
    case PLAN_TYPE.FREE:
      return "Free";
    case PLAN_TYPE.ADVANCED:
      // advanced plans will usually use planType = basic, plus, pro, etc. rather than this
      return "Advanced";
    case PLAN_TYPE.BUNDLE_SUBSCRIPTION:
      return planName;
    default:
      return planType;
  }
};

const getPricingText = activePricingPlans => {
  const filterDealerPlans =
    Array.isArray(activePricingPlans) &&
    activePricingPlans?.length &&
    activePricingPlans.filter(f => f?.dealerPriceChecked);

  let lowestPricingAmount;
  let lowestPricingPlanName;
  if (filterDealerPlans?.length) {
    const lowest = filterDealerPlans.reduce((min, plan) => {
      const amount =
        plan.type.toLowerCase() === PLAN_TYPE.SUBSCRIPTION
          ? plan?.subscriptionPrice
          : plan?.price;
      const isDealerSubscriptionPlan =
        plan.type.toLowerCase() === PLAN_TYPE.SUBSCRIPTION;

      if (!min || amount < min.amount) {
        return { amount, name: plan?.name, isDealerSubscriptionPlan };
      }
      return min;
    }, null);
    return {
      lowestPricingAmount: lowest?.amount,
      lowestPricingPlanName: lowest?.name,
      isDealerSubscriptionPlan: lowest?.isDealerSubscriptionPlan
    };
  } else {
    const planTypes =
      Array.isArray(activePricingPlans) &&
      activePricingPlans?.length &&
      activePricingPlans.map(f => {
        return f?.type?.toLowerCase();
      });

    if (planTypes?.length && planTypes.includes(PLAN_TYPE.FREE)) {
      lowestPricingAmount = 0;
      lowestPricingPlanName = "Free Plan";
      return { lowestPricingAmount, lowestPricingPlanName };
    }

    if (planTypes?.length && planTypes.includes(PLAN_TYPE.TRANSACTIONAL)) {
      lowestPricingAmount = 0;
      lowestPricingPlanName = "Transactional Plan";
      return { lowestPricingAmount, lowestPricingPlanName };
    }

    if (
      planTypes?.length &&
      planTypes.includes(PLAN_TYPE.ADVANCED && PLAN_TYPE.SUBSCRIPTION)
    ) {
      const filterAdvancedPlans =
        Array.isArray(activePricingPlans) &&
        activePricingPlans?.length &&
        activePricingPlans.filter(
          f => f?.type.toLowerCase() === PLAN_TYPE.ADVANCED
        );

      const sortAdvancedPricingPlans =
        filterAdvancedPlans?.length &&
        filterAdvancedPlans.sort((a, b) => a?.price - b?.price);

      const lowestAdvancedPlan =
        sortAdvancedPricingPlans?.length && sortAdvancedPricingPlans[0];

      const filterSubscriptionPlans =
        Array.isArray(activePricingPlans) &&
        activePricingPlans?.length &&
        activePricingPlans.filter(
          f => f?.type.toLowerCase() === PLAN_TYPE.SUBSCRIPTION
        );

      const sortSubscriptionPricingPlans =
        filterSubscriptionPlans?.length &&
        filterSubscriptionPlans.sort(
          (a, b) => a?.subscriptionPrice - b?.subscriptionPrice
        );

      const lowestSubscriptionPlan =
        sortSubscriptionPricingPlans?.length && sortSubscriptionPricingPlans[0];

      let result;

      if (
        lowestAdvancedPlan?.price < lowestSubscriptionPlan?.subscriptionPrice
      ) {
        result = lowestAdvancedPlan;
      } else {
        result = lowestSubscriptionPlan;
      }

      lowestPricingAmount = result?.price
        ? result?.price
        : result?.subscriptionPrice;

      lowestPricingPlanName = result?.price
        ? result?.name
        : "Subscription Plan";

      return { lowestPricingAmount, lowestPricingPlanName };
    }

    if (planTypes?.length && planTypes.includes(PLAN_TYPE.ADVANCED)) {
      const filterAdvancedPlans =
        Array.isArray(activePricingPlans) &&
        activePricingPlans?.length &&
        activePricingPlans.filter(
          f => f?.type?.toLowerCase() === PLAN_TYPE.ADVANCED
        );

      const sortArray = filterAdvancedPlans.sort((a, b) => a?.price - b?.price);

      const lowestPricingPlan = sortArray[0];

      lowestPricingAmount = lowestPricingPlan?.price;
      lowestPricingPlanName = lowestPricingPlan?.name;

      return { lowestPricingAmount, lowestPricingPlanName };
    }

    if (planTypes?.length && planTypes.includes(PLAN_TYPE.SUBSCRIPTION)) {
      const filterSubscriptionPlans =
        Array.isArray(activePricingPlans) &&
        activePricingPlans?.length &&
        activePricingPlans.filter(
          f => f?.type?.toLowerCase() === PLAN_TYPE.SUBSCRIPTION
        );

      const sortArray = filterSubscriptionPlans.sort(
        (a, b) => a?.subscriptionPrice - b?.subscriptionPrice
      );

      const lowestPricingPlan = sortArray[0];

      lowestPricingAmount = lowestPricingPlan?.subscriptionPrice;
      lowestPricingPlanName = "Subscription Plan";

      return { lowestPricingAmount, lowestPricingPlanName };
    }
  }
};

const formatNumbers = value => {
  return new Intl.NumberFormat("en-us", {
    maximumFractionDigits: 20
  }).format(value);
};

const formatCurrency = (value, locale = "en-US", currency = "USD") => {
  const numericValue = Number(value);
  const safeValue = Number.isFinite(numericValue) ? numericValue : 0;

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(safeValue);
};

const PRICING_PLAN_CTA = {
  ADD_TO_CART: "Add to Cart",
  SELECTED: "Selected",
  SELECT_PRICING_PLAN: "Select"
};

const getPlanCTA = (isAPIinCart, isSelected) => {
  const state =
    isSelected && isAPIinCart
      ? "selectedAndInCart"
      : isAPIinCart
      ? "inCart"
      : "notInCart";

  switch (state) {
    case "selectedAndInCart":
      return PRICING_PLAN_CTA.SELECTED;
    case "inCart":
      return PRICING_PLAN_CTA.SELECT_PRICING_PLAN;
    case "notInCart":
      return PRICING_PLAN_CTA.ADD_TO_CART;
    default:
      return PRICING_PLAN_CTA.ADD_TO_CART;
  }
};

const NO_PRICING_PLAN_TEXT =
  "The API provider does not have any pricing available at this time.";

const getAllEndpoints = (spec, isAsync) => {
  let allEndpointsTexts = [];
  if (spec) {
    if (isAsync) {
      let channels = spec?.channels || {};
      allEndpointsTexts = Object.keys(channels);
    } else {
      let paths = spec?.paths || [];
      let pathResources = Object.keys(paths);
      pathResources.forEach(resource => {
        let methods = Object.keys(paths[resource]) || [];
        methods.forEach(method => {
          const summary =
            paths[resource][method] && paths[resource][method].summary
              ? paths[resource][method].summary
              : resource.replace("/", "");
          const endpointText = `${method.toUpperCase()} ${summary}`;
          allEndpointsTexts.push(endpointText);
        });
      });
    }
  }
  return allEndpointsTexts;
};

const parsePrice = plan =>
  Number(plan?.price) || Number(plan?.subscriptionPrice) || 0;

const sortByPrice = (a, b) => {
  const priceA = parsePrice(a);
  const priceB = parsePrice(b);
  return priceA - priceB;
};

const getPlansToShowForAPI = (allPricingPlans = []) => {
  if (!Array.isArray(allPricingPlans) || allPricingPlans.length === 0) {
    return [];
  }

  const dealerPlans = [];
  const otherPlans = [];

  for (const plan of allPricingPlans) {
    if (plan.status === PLAN_STATUSES.ACTIVE) {
      if (plan.dealerPriceChecked) {
        dealerPlans.push(plan);
      } else {
        otherPlans.push(plan);
      }
    }
  }
  // return only dealer plans if any exist, else return other plans
  return dealerPlans.length
    ? dealerPlans.sort(sortByPrice)
    : otherPlans.sort(sortByPrice);
};

const PLAN_TYPE_ORDER = {
  custom: 1,
  basic: 2,
  essentials: 3,
  plus: 4,
  pro: 5,
  free: 6,
  tiered: 7,
  subscription: 8
};

export {
  PLAN_TYPE,
  PRICING_PLAN_CTA,
  getPlanName,
  formatNumbers,
  formatCurrency,
  getPlanCTA,
  PLAN_STATUSES,
  PLAN_VISIBILITY,
  NO_PRICING_PLAN_TEXT,
  PLAN_TYPE_ORDER,
  getPricingText,
  getAllEndpoints,
  getPlanCardText,
  getPlansToShowForAPI
};
